import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Edit2, Check, MessageSquare } from 'lucide-react';
import type { IssueDetail, Severity, Status } from '@/models/issue';
import { SEVERITIES, STATUSES } from '@/models/issue';
import { issueApi } from '@/services/issueApi';
import { storageApi } from '@/services/storageApi';
import { useIssue } from '@/controllers/useIssues';
import { SeverityBadge, StatusBadge, Tag } from './components/AlarmBadge';
import { Markdown } from './components/Markdown';

export function DetailView() {
  const { id } = useParams<{ id: string }>();
  const issueId = Number(id);
  const { issue, loading, error: loadError, refresh } = useIssue(issueId);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<IssueDetail>>({});
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [opError, setOpError] = useState('');

  if (loading && !issue) {
    return (
      <div className="py-10 text-center text-[13px] text-[var(--text-muted)] font-mono">lädt…</div>
    );
  }
  if (loadError || !issue) {
    return (
      <div className="p-3 text-[12px] border-l-2"
        style={{
          borderColor: 'var(--color-error)',
          background: 'var(--color-error-bg)',
          color: 'var(--color-error)',
        }}>
        {loadError || 'Issue nicht gefunden'}
      </div>
    );
  }

  const current = { ...issue, ...draft } as IssueDetail;

  async function saveEdits() {
    if (!Object.keys(draft).length) {
      setEditing(false);
      return;
    }
    try {
      await issueApi.patch(issueId, draft);
      await refresh();
      setDraft({});
      setEditing(false);
    } catch (e) {
      setOpError(e instanceof Error ? e.message : 'Update fehlgeschlagen');
    }
  }

  async function submitComment() {
    if (!comment.trim() || posting) return;
    setPosting(true);
    try {
      await issueApi.comment(issueId, comment.trim());
      await refresh();
      setComment('');
    } catch (e) {
      setOpError(e instanceof Error ? e.message : 'Kommentar konnte nicht gespeichert werden');
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="max-w-4xl">
      <Link
        to="/list"
        className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.15em] text-[var(--text-muted)] hover:text-[var(--text-primary)] mb-6"
      >
        <ArrowLeft size={12} strokeWidth={1.8} /> Liste
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-6 mb-6">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)] font-mono mb-1">
            issue.#{String(issue.id).padStart(3, '0')}
          </div>
          {editing ? (
            <input
              value={current.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="w-full text-[24px] tracking-tight"
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 400,
                background: 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--border)',
                padding: '4px 0',
                outline: 'none',
              }}
            />
          ) : (
            <h1
              className="text-[24px] tracking-tight leading-tight"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, color: 'var(--text-body)' }}
            >
              {current.title}
            </h1>
          )}
        </div>
        <button
          className="btn-primary inline-flex items-center gap-2"
          onClick={() => (editing ? saveEdits() : setEditing(true))}
          style={editing ? { background: 'var(--accent)', color: 'white' } : undefined}
        >
          {editing ? <Check size={14} strokeWidth={1.8} /> : <Edit2 size={14} strokeWidth={1.8} />}
          {editing ? 'Speichern' : 'Bearbeiten'}
        </button>
      </div>

      {/* Meta-row */}
      <div className="flex flex-wrap gap-2 items-center mb-6">
        {editing ? (
          <>
            <select
              value={current.severity}
              onChange={(e) => setDraft({ ...draft, severity: e.target.value as Severity })}
            >
              {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={current.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value as Status })}
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input
              className="input"
              style={{ width: 200 }}
              placeholder="component"
              value={current.component || ''}
              onChange={(e) => setDraft({ ...draft, component: e.target.value || null })}
            />
          </>
        ) : (
          <>
            <SeverityBadge severity={current.severity} />
            <StatusBadge status={current.status} />
            {current.component && <Tag>{current.component}</Tag>}
          </>
        )}
      </div>

      {/* Tags */}
      {current.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-6">
          {current.tags.map((t) => (
            <Tag key={t}>{t}</Tag>
          ))}
        </div>
      )}

      {/* Description */}
      <div className="section-title">Beschreibung</div>
      <div
        className="mb-8 p-5"
        style={{ background: 'var(--bg-inset)', border: '1px solid var(--border)' }}
      >
        <Markdown>{current.description}</Markdown>
      </div>

      {/* Field grid */}
      <div className="section-title">Details</div>
      <dl className="grid mb-8 text-[12px] font-mono" style={{ gridTemplateColumns: 'max-content 1fr', gap: '8px 32px' }}>
        <dt className="text-[var(--text-muted)] uppercase tracking-[0.1em] text-[10px] self-center">Reporter</dt>
        <dd>{current.reporter} <span className="text-[var(--text-muted)]">({current.reporter_type})</span></dd>

        {current.assignee && (<>
          <dt className="text-[var(--text-muted)] uppercase tracking-[0.1em] text-[10px] self-center">Assignee</dt>
          <dd>{current.assignee}</dd>
        </>)}

        <dt className="text-[var(--text-muted)] uppercase tracking-[0.1em] text-[10px] self-center">Tenant</dt>
        <dd>{current.tenant_id}</dd>

        {current.file_pointer && (<>
          <dt className="text-[var(--text-muted)] uppercase tracking-[0.1em] text-[10px] self-center">File</dt>
          <dd>{current.file_pointer}</dd>
        </>)}

        {current.resolution_commit && (<>
          <dt className="text-[var(--text-muted)] uppercase tracking-[0.1em] text-[10px] self-center">Commit</dt>
          <dd>{current.resolution_commit}</dd>
        </>)}

        {current.resolution_summary && (<>
          <dt className="text-[var(--text-muted)] uppercase tracking-[0.1em] text-[10px] self-center">Summary</dt>
          <dd style={{ fontFamily: 'var(--font-body)' }}>
            <Markdown compact>{current.resolution_summary}</Markdown>
          </dd>
        </>)}

        {current.fixed_at && (<>
          <dt className="text-[var(--text-muted)] uppercase tracking-[0.1em] text-[10px] self-center">Fixed</dt>
          <dd>
            {new Date(current.fixed_at).toLocaleString('de-DE')}{' '}
            <span className="text-[var(--text-muted)]">by {current.fixed_by}</span>
          </dd>
        </>)}

        <dt className="text-[var(--text-muted)] uppercase tracking-[0.1em] text-[10px] self-center">Created</dt>
        <dd>{new Date(current.created_at).toLocaleString('de-DE')}</dd>
      </dl>

      {/* Attachments */}
      {issue.attachments.length > 0 && (
        <>
          <div className="section-title">Attachments</div>
          <div className="flex flex-wrap gap-3 mb-8">
            {issue.attachments.map((a) => {
              const url = storageApi.mediaUrl(a.storage_id);
              return (
                <a
                  key={a.id}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="block"
                  style={{ border: '1px solid var(--border)' }}
                >
                  <img
                    src={url}
                    alt={a.caption || a.role}
                    style={{ maxWidth: 220, maxHeight: 150, display: 'block', objectFit: 'cover' }}
                    onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
                  />
                  <div className="px-2 py-1 text-[10px] font-mono text-[var(--text-muted)] border-t border-[var(--border)]">
                    {a.role} · storage/{a.storage_id}
                  </div>
                </a>
              );
            })}
          </div>
        </>
      )}

      {opError && (
        <div className="mb-6 p-3 text-[12px] border-l-2"
          style={{
            borderColor: 'var(--color-error)',
            background: 'var(--color-error-bg)',
            color: 'var(--color-error)',
          }}>
          {opError}
        </div>
      )}

      {/* Comments */}
      <div className="section-title flex items-center gap-2">
        <MessageSquare size={13} strokeWidth={1.8} />
        Comments · {issue.comments.length}
      </div>
      {issue.comments.map((c) => (
        <div key={c.id} className="py-4 border-b border-[var(--border)]">
          <div className="flex gap-3 text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-wider mb-2">
            <span>{c.author}</span>
            <span>·</span>
            <span>{new Date(c.created_at).toLocaleString('de-DE')}</span>
          </div>
          <div className="text-[14px]"><Markdown compact>{c.body}</Markdown></div>
        </div>
      ))}
      <div className="mt-6">
        <textarea
          placeholder="Kommentar hinzufügen…"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={posting}
          className="w-full p-3 text-[14px]"
          style={{
            minHeight: '5rem',
            background: 'var(--bg-inset)',
            border: '1px solid var(--border)',
            borderRadius: 0,
            outline: 'none',
            fontFamily: 'var(--font-body)',
          }}
        />
        <div className="text-right mt-2">
          <button
            className="btn-primary"
            onClick={submitComment}
            disabled={!comment.trim() || posting}
            style={{ background: 'var(--accent)', color: 'white' }}
          >
            {posting ? 'sende…' : 'Senden'}
          </button>
        </div>
      </div>
    </div>
  );
}
